import type { ValidationResult } from "@react-types/shared";
import { clsx } from "clsx";
import { type FC, type PropsWithChildren } from "react";
import { type AriaRadioGroupProps, useRadioGroup } from "react-aria";
import { useRadioGroupState } from "react-stately";

import formStyle from "../../styles/form.module.css";
import { Badge } from "../Badge/Badge.tsx";
import { Description } from "../Description/Description.tsx";
import { Label } from "../Label/Label.tsx";
import { HStack } from "../Stack/HStack.tsx";
import { Spacer } from "../Stack/Spacer.tsx";
import { VStack } from "../Stack/VStack.tsx";
import { RadioContext } from "./settings.ts";

export const RadioGroup: FC<PropsWithChildren<AriaRadioGroupProps>> = (props) => {
  const state = useRadioGroupState(props);
  const { radioGroupProps, labelProps, descriptionProps, errorMessageProps } = useRadioGroup(props, state);

  return (
    <VStack spacing={2} {...radioGroupProps}>
      {props.label != undefined && (
        <HStack style={{ alignItems: "baseline" }}>
          <Label {...labelProps}>{props.label}</Label>
          <Spacer />
          {!!props.isReadOnly && <Badge intent="light" large inline label="read only" style={{ flex: "0 0 auto" }} />}
          {!props.isRequired && !props.isReadOnly && (
            <Badge intent="light" large inline label="optional" style={{ flex: "0 0 auto" }} />
          )}
        </HStack>
      )}
      <Description
        {...descriptionProps}
        data-testid="checkbox-field-description"
        className={clsx(formStyle.input_layout_column1, {
          [formStyle.description_with_tag]: props.label != undefined && (props.isReadOnly || !props.isRequired),
        })}
      >
        {props.description}
      </Description>
      <VStack spacing={3} style={{ marginTop: 2 }}>
        <RadioContext.Provider value={state}>{props.children}</RadioContext.Provider>
      </VStack>
      {props.errorMessage != undefined && !!props.isInvalid && !props.isDisabled && (
        <div role="alert" {...errorMessageProps} className={formStyle.error_message} data-testid="number-field-error">
          {typeof props.errorMessage === "function" ? props.errorMessage({} as ValidationResult) : props.errorMessage}
        </div>
      )}
    </VStack>
  );
};
